import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const NAV_LINKS = [{
  name: 'Inicio',
  href: '/',
  isRoute: true
}, {
  name: 'Nosotros',
  href: '/nosotros',
  isRoute: true
}, {
  name: 'Cómo Funciona',
  href: '/#como-funciona',
  isRoute: false
}, {
  name: 'Participantes',
  href: '/participantes',
  isRoute: true,
  submenu: [{
    name: 'Organizaciones Participantes',
    href: '/participantes#organizaciones'
  }, {
    name: 'Aliados Estratégicos',
    href: '/participantes#aliados'
  }]
}];
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const handleNavClick = (href: string, isRoute: boolean) => {
    if (isRoute) {
      // Check if it has a hash (e.g. /participantes#organizaciones)
      const [path, hash] = href.split('#');
      navigate(path);
      if (hash) {
        setTimeout(() => {
          document.getElementById(hash)?.scrollIntoView({
            behavior: 'smooth'
          });
        }, 150);
      } else {
        window.scrollTo(0, 0);
      }
    } else {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const id = href.replace('/#', '');
          document.getElementById(id)?.scrollIntoView({
            behavior: 'smooth'
          });
        }, 100);
      } else {
        const id = href.replace('/#', '');
        document.getElementById(id)?.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }
  };
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-brand-navy/95 backdrop-blur-md shadow-lg py-3' : 'bg-brand-navy py-5'}`} data-id="element-570">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-571">
        <div className="flex justify-between items-center" data-id="element-572">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" aria-label="RedOrg Inicio" onClick={() => window.scrollTo(0, 0)} data-id="element-573">
            <div className="bg-white/95 px-3 py-1.5 rounded-full group-hover:bg-white transition-colors" data-id="element-574">
              <img src="https://cdn.magicpatterns.com/uploads/78sjHca3jZk8xTuZahNMSg/Logo_REDORG.png" alt="RedOrg Logo" className="h-9 w-auto object-contain" data-id="element-575" />
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-id="element-576">
            {NAV_LINKS.map(link => link.submenu ? <div key={link.name} className="relative" ref={dropdownRef} data-id="element-577">
                  <button onClick={() => setDropdownOpen(!dropdownOpen)} className="flex items-center gap-1 text-white/90 hover:text-brand-gold font-medium text-sm transition-colors bg-transparent border-none cursor-pointer" data-id="element-578">
                    {link.name}
                    <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-579" />
                  </button>
                  <AnimatePresence data-id="element-580">
                    {dropdownOpen && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 8
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden" data-id="element-581">
                        {link.submenu.map(sub => <button key={sub.name} onClick={() => {
                  setDropdownOpen(false);
                  handleNavClick(sub.href, true);
                }} className="block w-full text-left px-5 py-3.5 text-sm font-medium text-brand-navy hover:bg-brand-lightGray hover:text-brand-royal transition-colors bg-transparent border-none cursor-pointer" data-id="element-582">
                            {sub.name}
                          </button>)}
                      </motion.div>}
                  </AnimatePresence>
                </div> : <button key={link.name} onClick={() => handleNavClick(link.href, link.isRoute)} className="text-white/90 hover:text-brand-gold font-medium text-sm transition-colors bg-transparent border-none cursor-pointer" data-id="element-583">
                  {link.name}
                </button>)}
            <a href="https://adminorg.redorg.net" target="_blank" rel="noopener noreferrer" className="bg-brand-gold hover:bg-yellow-400 text-brand-navy font-heading font-semibold px-5 py-2.5 rounded-full transition-colors shadow-sm hover:shadow-md cursor-pointer inline-block" data-id="element-584">
              Acceso a Plataformas
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button className="md:hidden text-white p-2 focus:outline-none" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu" data-id="element-585">
            {isOpen ? <XIcon className="h-6 w-6" data-id="element-586" /> : <MenuIcon className="h-6 w-6" data-id="element-587" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence data-id="element-588">
        {isOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-brand-navy border-t border-white/10 overflow-hidden" data-id="element-589">
            <div className="px-4 pt-2 pb-6 space-y-1 flex flex-col" data-id="element-590">
              {NAV_LINKS.map(link => link.submenu ? <div key={link.name} data-id="element-591">
                    <button onClick={() => setMobileSubmenuOpen(!mobileSubmenuOpen)} className="flex items-center justify-between w-full px-3 py-4 text-base font-medium text-white hover:text-brand-gold hover:bg-white/5 rounded-md transition-colors bg-transparent border-none cursor-pointer" data-id="element-592">
                      {link.name}
                      <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${mobileSubmenuOpen ? 'rotate-180' : ''}`} data-id="element-593" />
                    </button>
                    <AnimatePresence data-id="element-594">
                      {mobileSubmenuOpen && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden" data-id="element-595">
                          {link.submenu.map(sub => <button key={sub.name} onClick={() => {
                  setIsOpen(false);
                  setMobileSubmenuOpen(false);
                  handleNavClick(sub.href, true);
                }} className="block w-full text-left pl-8 pr-3 py-3 text-sm font-medium text-white/70 hover:text-brand-gold hover:bg-white/5 rounded-md transition-colors bg-transparent border-none cursor-pointer" data-id="element-596">
                              {sub.name}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div> : <button key={link.name} onClick={() => {
            setIsOpen(false);
            handleNavClick(link.href, link.isRoute);
          }} className="block w-full text-left px-3 py-4 text-base font-medium text-white hover:text-brand-gold hover:bg-white/5 rounded-md transition-colors bg-transparent border-none cursor-pointer" data-id="element-597">
                    {link.name}
                  </button>)}
              <a href="https://adminorg.redorg.net" target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)} className="mt-4 block w-full text-center bg-brand-gold text-brand-navy font-heading font-semibold px-5 py-3 rounded-md transition-colors cursor-pointer no-underline" data-id="element-598">
                Acceso a Plataformas
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}